﻿import {expressQuerySelector, hasClass} from '../common/html';
import {createClickableElement} from './button';

export interface ICreateCollapsableDependencies {
	readonly onBeforeOpen?: () => Promise<void>;
	readonly onBeforeClose?: () => Promise<void>;
	readonly titleElSelector: string;
	readonly scrollIntoViewAfterOpen: boolean;
	readonly  openClass?: string;
}

export interface ICollapsable {
	dispose(): void;

	show(): void;

	close(): void;

	hide(): void;

	open(scrollIntoView?: boolean): void;
}

export function createCollapsable(container: HTMLElement, deps: ICreateCollapsableDependencies): ICollapsable {
	const {onBeforeOpen, onBeforeClose, titleElSelector, scrollIntoViewAfterOpen, openClass} = deps;
	const OPEN_CLASS = openClass ? openClass : "is-collapse-open";
	const titleEl = expressQuerySelector<HTMLElement>(container, titleElSelector, true);

	const closeWithBefore = () => {
		if (!onBeforeClose) {
			close();
			return Promise.resolve();
		}

		return onBeforeClose().then(() => close());
	};

	const openWithBefore = (scrollIntoView = false) => {
		if (!onBeforeOpen) {
			open();
			return Promise.resolve();
		}

		return onBeforeOpen().then(() => open(scrollIntoView));
	};

	const close = () => container.classList.remove(OPEN_CLASS);
	const open = (shouldScrollIntoView?: boolean) => {
		container.classList.add(OPEN_CLASS);
		if (scrollIntoViewAfterOpen || shouldScrollIntoView)
			titleEl.scrollIntoView({behavior: "smooth", block: "start"});
	};

	const isOpen = () => hasClass(container, OPEN_CLASS);

	const onClick = () => !isOpen() ? openWithBefore() : closeWithBefore();

	const button = createClickableElement(titleEl, onClick);

	return {
		dispose: button.dispose,
		show: () => container.classList.remove('u-hide'),
		hide: () => container.classList.add('u-hide'),
		close,
		open: (scrollIntoView?: boolean) => openWithBefore(scrollIntoView)
	};
}
